import { i18n } from '@/i18n';
import {
  type ArcualInventoryItem,
  ArcualDocumentSignatureStatus,
  mediaUrl,
  PurchaseRequestStatus,
} from '@arcual/lib-app-platform';
import {
  NeonLabelColor,
  type NeonLabelModel,
} from '@arcual/lib-app-neon';
import { listingsMessages } from '@/components/listings/Listings.messages';

const { t } = i18n.global;

export const getOfferColor = (status: PurchaseRequestStatus) => {
  switch (status) {
    case PurchaseRequestStatus.OPEN:
      return NeonLabelColor.Warn;
    case PurchaseRequestStatus.DECLINED:
    case PurchaseRequestStatus.WITHDRAWN:
      return NeonLabelColor.HighContrast;
    case PurchaseRequestStatus.SALES_AGREEMENT_SENT:
    case PurchaseRequestStatus.SALES_AGREEMENT_PAID:
      return NeonLabelColor.Success;
  }
};

export const getOfferStatusTitle = (status: PurchaseRequestStatus) => {
  switch (status) {
    case PurchaseRequestStatus.OPEN:
      return t('storefront.app.offers.status.pending');
    case PurchaseRequestStatus.DECLINED:
      return t('storefront.app.offers.status.declined');
    case PurchaseRequestStatus.WITHDRAWN:
      return t('storefront.app.offers.status.withdrawn');
    case PurchaseRequestStatus.SALES_AGREEMENT_SENT:
      return t('storefront.app.offers.status.accepted');
    case PurchaseRequestStatus.SALES_AGREEMENT_PAID:
      return t('storefront.app.offers.status.paid');
  }
};


export const getSignatureStatusColor = (status: ArcualDocumentSignatureStatus) => {
  return status === ArcualDocumentSignatureStatus.SIGNED ? NeonLabelColor.Success : NeonLabelColor.Warn;
};

export const getArtworkLabels = (artwork?: ArcualInventoryItem) => {
  const labels: NeonLabelModel[] = [];

  if (artwork?.digitalDossierState?.coaSignatureStatus === ArcualDocumentSignatureStatus.SIGNED) {
    labels.push({
      label: listingsMessages.coaTitleShort,
      color: getSignatureStatusColor(artwork.digitalDossierState.coaSignatureStatus),
      icon: 'arcual-logo-outline',
    });
  }

  if (artwork?.digitalDossierState?.hasOtherDocuments) {
    labels.push({
      label: listingsMessages.supportingDocumentsTitle,
      color: NeonLabelColor.Success,
      icon: 'folder-lock-1',
    });
  }

  if (artwork?.digitalDossierState?.resaleTermsSignatureStatus === ArcualDocumentSignatureStatus.SIGNED) {
    labels.push({
      label: listingsMessages.resaleTermsTitle,
      color: getSignatureStatusColor(artwork.digitalDossierState.resaleTermsSignatureStatus),
      icon: 'business-contract-handshake-sign',
    });
  }

  return labels;
};

export const createArtworkDetailSharing = (artwork?: ArcualInventoryItem) => {
  if (artwork) {
    return {
      meta: [
        {
          property: 'og:title',
          content: `${artwork.artistName} - ${artwork.title}, ${artwork.year}`,
        },
        {
          property: 'og:url',
          content: window.location.href,
        },
        {
          property: 'og:image',
          content: mediaUrl(artwork.images[0].url, 1200, 1200),
        },
        {
          property: 'og:image:type',
          content: 'image/webp',
        },
        {
          property: 'og:image:width',
          content: '1200',
        },
        {
          property: 'og:image:height',
          content: '1200',
        },
        {
          property: 'og:image:alt',
          content: artwork.images[0].description || artwork.title,
        },
        {
          property: 'og:description',
          content: artwork.description,
        },
        {
          name: 'twitter:title',
          content: `${artwork.artistName} - ${artwork.title}, ${artwork.year}`,
        },
        {
          name: 'twitter:description',
          content: artwork.description,
        },
        {
          name: 'twitter:image',
          content: mediaUrl(artwork.images[0].url, 1200, 1200),
        },
      ],
    };
  }

  return { meta: [] };
};
